import { useState, useEffect } from 'react'
import { Receipt, Download, Search, Filter, Calendar, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { motion } from 'framer-motion'
import { formatDate } from '@/lib/utils'
import { dashboardApi } from '@/services/api'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

export function DashboardTransactions() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('all') // all, subscription, article, donation, purchase
  const [page, setPage] = useState(1)

  // Fetch article purchases from API
  const { data: articlePurchasesData, isLoading, error } = useQuery({
    queryKey: ['articlePurchases', page],
    queryFn: async () => {
      const response = await dashboardApi.getArticlePurchases(page)
      return response.data
    },
    onError: (err) => {
      toast.error('Failed to load article purchases')
    }
  })

  // Fetch donations from API
  const { data: donationsData } = useQuery({
    queryKey: ['donations', page],
    queryFn: async () => {
      const response = await dashboardApi.getDonations(page)
      return response.data
    },
    onError: (err) => {
      console.error('Failed to load donations:', err)
    }
  })

  // Fetch purchase transactions (automatic $10+ charges)
  const { data: purchaseTransactionsData } = useQuery({
    queryKey: ['purchaseTransactions', page],
    queryFn: async () => {
      const response = await dashboardApi.getPurchaseTransactions(page)
      return response.data
    },
    onError: (err) => {
      console.error('Failed to load purchase transactions:', err)
    }
  })

  // Transform article purchases to transaction format
  const articlePurchases = (articlePurchasesData?.results || []).map(article => ({
    id: article.alias,
    date: article.purchases.time,
    description: `Article: "${article.title}"`,
    amount: parseFloat(article.purchases.amount),
    status: 'completed',
    type: 'article',
    paymentMethod: 'Card',
    article_title: article.title,
    article_slug: article.slug,
    article_link: `/posts/${article.slug}/`,
    feeder_display_name: article.feeder.display_name,
    feeder_link: `/provider/${article.feeder.slug}/`,
    excerpt: article.excerpt
  }))

  // Transform donations to transaction format
  const donationTransactions = (donationsData || []).map(donation => ({
    id: donation.id,
    date: donation.donated_at,
    description: donation.article_title 
      ? `Donation for "${donation.article_title}"`
      : `Donation to ${donation.feeder_display_name}`,
    amount: parseFloat(donation.amount),
    status: 'completed',
    type: 'donation',
    paymentMethod: 'Card',
    feeder_display_name: donation.feeder_display_name,
    feeder_link: donation.feeder_link,
    article_title: donation.article_title,
    article_link: donation.article_link,
    currency: donation.currency?.toUpperCase() || 'USD'
  }))

  // Transform purchase transactions (automatic $10+ charges from Stripe)
  const stripeTransactions = (purchaseTransactionsData?.results || []).map(txn => ({
    id: txn.alias,
    date: txn.created_at,
    description: `Automatic charge for article purchases ($${parseFloat(txn.amount).toFixed(2)} accumulated)`,
    amount: parseFloat(txn.amount),
    status: txn.status === 'completed' ? 'completed' : txn.status === 'pending' ? 'pending' : 'failed',
    type: 'purchase',
    paymentMethod: 'Card (Auto-charge)',
    transaction_id: txn.transaction_id,
    stripe_payment_intent: txn.transaction_id
  }))

  // Combine all transactions and sort by date (newest first)
  const allTransactions = [...articlePurchases, ...donationTransactions, ...stripeTransactions]
    .sort((a, b) => new Date(b.date) - new Date(a.date))

  // Use real data only - no mock data
  const transactions = allTransactions

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = txn.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesFilter = filterType === 'all' || txn.type === filterType
    return matchesSearch && matchesFilter
  })

  const totalSpent = transactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0)

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700'
    }
    return variants[status] || variants.completed
  }

  const getTypeBadge = (type) => {
    const variants = {
      subscription: 'bg-slate-100 text-slate-700',
      article: 'bg-purple-100 text-purple-700',
      donation: 'bg-green-100 text-green-700',
      purchase: 'bg-orange-100 text-orange-700'
    }
    return variants[type] || variants.article
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-primary" />
            Transaction History
          </h1>
          <p className="text-gray-600">View and manage your purchase history</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Receipt className="w-8 h-8 text-primary" />
          Transaction History
        </h1>
        <p className="text-gray-600">View and manage your purchase history</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          data-logo-box
          className="rounded-xl p-6 text-white transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-100">Total Spent</span>
            <DollarSign className="w-5 h-5 text-slate-200" />
          </div>
          <p className="text-3xl font-bold">${totalSpent.toFixed(2)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">Total Transactions</span>
            <Receipt className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{transactions.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">This Month</span>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ${transactions.slice(0, 2).reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
          </p>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('all')}
            >
              All
            </Button>
            <Button
              variant={filterType === 'article' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('article')}
            >
              Articles
            </Button>
            <Button
              variant={filterType === 'donation' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('donation')}
            >
              Donations
            </Button>
            <Button
              variant={filterType === 'purchase' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('purchase')}
            >
              Auto-Charges
            </Button>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filteredTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try adjusting your search or filters' : 'Your transaction history will appear here'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTransactions.map((transaction, index) => (
              <motion.div
                key={transaction.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{transaction.description}</h3>
                      <Badge className={getTypeBadge(transaction.type)}>
                        {transaction.type}
                      </Badge>
                      <Badge className={getStatusBadge(transaction.status)}>
                        {transaction.status}
                      </Badge>
                    </div>
                    
                    {/* Show article purchase details */}
                    {transaction.type === 'article' && (
                      <div className="mb-2">
                        {transaction.feeder_display_name && (
                          <div className="text-sm text-gray-600">
                            from{' '}
                            <a
                              href={transaction.feeder_link}
                              className="text-primary hover:underline font-medium"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {transaction.feeder_display_name}
                            </a>
                          </div>
                        )}
                        {transaction.excerpt && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {transaction.excerpt}
                          </p>
                        )}
                        {transaction.article_link && (
                          <a
                            href={transaction.article_link}
                            className="text-sm text-primary hover:underline mt-1 inline-block"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Read article →
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Show donation details */}
                    {transaction.type === 'donation' && (
                      <div className="mb-2">
                        {transaction.feeder_display_name && (
                          <a
                            href={transaction.feeder_link}
                            className="text-primary hover:underline font-medium text-sm"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            to {transaction.feeder_display_name}
                          </a>
                        )}
                        {transaction.article_title && transaction.article_link && (
                          <div className="text-sm text-gray-600 mt-1">
                            For article:{' '}
                            <a
                              href={transaction.article_link}
                              className="text-primary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {transaction.article_title}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(transaction.date)}
                      </span>
                      <span>•</span>
                      <span>{transaction.paymentMethod}</span>
                      {transaction.currency && transaction.type === 'donation' && (
                        <>
                          <span>•</span>
                          <span>{transaction.currency}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      ${transaction.amount.toFixed(2)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {(articlePurchasesData?.next || articlePurchasesData?.previous || donationsData?.next || donationsData?.previous) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">
            {articlePurchasesData?.count && (
              <span>
                Showing {((page - 1) * 10) + 1} - {Math.min(page * 10, articlePurchasesData.count)} of {articlePurchasesData.count} transactions
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={!articlePurchasesData?.previous && !donationsData?.previous}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md">
              Page {page}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={!articlePurchasesData?.next && !donationsData?.next}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Export Button */}
      <div className="mt-6 flex justify-end">
        <Button variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Export All Transactions
        </Button>
      </div>
    </div>
  )
}
